/**
 * Formatea fechas o timestamps en textos legibles o relativos ("hace 5 min", "hace 2 horas", etc.)
 */
export const formatRelativeTime = dateStr => {
  if (!dateStr) return 'Reciente';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Reciente';

    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'Hace un momento';
    if (diffMins < 60) return `hace ${diffMins} min`;
    if (diffHours < 24) return `hace ${diffHours} h`;
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `hace ${diffDays} días`;

    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  } catch {
    return 'Reciente';
  }
};

/**
 * Obtiene iniciales a partir de un nombre o teléfono
 */
export const getTicketInitials = (name, phone) => {
  if (name && typeof name === 'string' && name.trim()) {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (phone && typeof phone === 'string') {
    return phone.slice(-2);
  }
  return 'TC';
};

/**
 * Normaliza la información del estado y colores según iEstadoTicket o status
 */
export const getTicketStatusInfo = estado => {
  // 1: Abierto/Pendiente, 2: Cerrado/Resuelto, 3: En progreso
  const st = Number(estado);
  if (st === 1 || estado === 'Pendiente' || estado === 'Abierto') {
    return {
      label: 'Pendiente',
      bgColor: '#ffedd5',
      textColor: '#c2410c',
      accentColor: '#f59e0b',
      hasWarning: true
    };
  }
  if (st === 3 || estado === 'En Progreso') {
    return {
      label: 'En Progreso',
      bgColor: '#e0f2fe',
      textColor: '#0369a1',
      accentColor: '#0ea5e9',
      hasWarning: false
    };
  }
  if (st === 2 || estado === 'Cerrado' || estado === 'Resuelto') {
    return {
      label: 'Resuelto',
      bgColor: '#dcfce7',
      textColor: '#15803d',
      accentColor: '#10b981',
      hasWarning: false
    };
  }

  return {
    label: 'Abierto',
    bgColor: '#f1f5f9',
    textColor: '#475569',
    accentColor: '#94a3b8',
    hasWarning: false
  };
};

/**
 * Calcula el tiempo transcurrido en espera sin respuesta a partir de dCreatedDate
 */
export const formatWaitingTime = createdDateStr => {
  if (!createdDateStr) return null;
  try {
    const created = new Date(createdDateStr);
    if (isNaN(created.getTime())) return null;

    const diffMs = new Date() - created;
    if (diffMs < 0) return 'Reciente';

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return '< 1 min';
    if (diffMins < 60) return `${diffMins} min`;

    const diffHours = Math.floor(diffMins / 60);
    const remMins = diffMins % 60;
    if (diffHours < 24) {
      return remMins > 0 ? `${diffHours}h ${remMins}m` : `${diffHours}h`;
    }

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ${diffHours % 24}h`;
  } catch {
    return null;
  }
};

/**
 * Formatea minutos exactos a formato legible ("X min", "Xh Xm", "Xd Xh")
 */
export const formatDurationMinutes = mins => {
  if (mins === null || mins === undefined || isNaN(mins)) return null;
  const m = Number(mins);
  if (m < 1) return '< 1 min';
  if (m < 60) return `${m} min`;
  const hours = Math.floor(m / 60);
  const remMins = m % 60;
  if (hours < 24) {
    return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
};

/**
 * Calcula los minutos transcurridos desde una fecha (creación o inicio)
 */
export const getTicketElapsedMinutes = createdDateStr => {
  if (!createdDateStr) return 0;
  try {
    const created = new Date(createdDateStr);
    if (isNaN(created.getTime())) return 0;
    const diffMs = new Date() - created;
    return Math.max(0, Math.floor(diffMs / 60000));
  } catch {
    return 0;
  }
};

/**
 * Obtiene el tiempo activo y clasificación de urgencia del ticket
 * - timeClass: 'danger' (>= 180 min / 3h), 'warning' (>= 60 min / 1h), 'normal' (< 60 min)
 * - isTooLong: true si excede 3 horas activo
 */
export const getTicketUrgencyInfo = (createdDateStr, estadoTicket) => {
  const estado = Number(estadoTicket);
  // Resuelto o Cerrado (2) no tiene tiempo activo de urgencia
  if (estado === 2) {
    return { elapsedMinutes: 0, timeFormatted: null, timeClass: 'normal', isTooLong: false };
  }

  if (!createdDateStr) {
    return { elapsedMinutes: 0, timeFormatted: null, timeClass: 'normal', isTooLong: false };
  }

  const elapsedMinutes = getTicketElapsedMinutes(createdDateStr);
  const timeFormatted = formatDurationMinutes(elapsedMinutes);
  const isTooLong = elapsedMinutes >= 180;
  const isWarning = elapsedMinutes >= 60 && !isTooLong;
  const timeClass = isTooLong ? 'danger' : isWarning ? 'warning' : 'normal';

  return {
    elapsedMinutes,
    timeFormatted,
    timeClass,
    isTooLong
  };
};
